import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { nip19 } from 'nostr-tools';

import type { NostrEvent } from '@nostrify/nostrify';
import { PUZZLE_KIND, PUZZLE_TAG, type PuzzleData, validatePuzzle } from '@/lib/puzzle';
import { useNostrPublish } from './useNostrPublish';

/** Combine an abort signal with a timeout signal. Returns the original if no timeout. */
function withTimeout(signal: AbortSignal | undefined, ms: number): AbortSignal {
  const timeoutSignal = AbortSignal.timeout(ms);
  if (!signal) return timeoutSignal;
  return AbortSignal.any([signal, timeoutSignal]);
}

export interface PuzzleEvent {
  event: NostrEvent;
  puzzle: PuzzleData;
  /** NIP-19 naddr identifier for sharing. */
  naddr: string;
  /** d-tag identifier. */
  dTag: string;
  /** Author pubkey. */
  pubkey: string;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

/** Parse a Nostr event into a PuzzleEvent. Returns null if invalid. */
export function parsePuzzleEvent(event: NostrEvent): PuzzleEvent | null {
  if (event.kind !== PUZZLE_KIND) return null;

  const dTag = event.tags.find(([n]) => n === 'd')?.[1];
  if (!dTag) return null;

  let puzzle: PuzzleData;
  try {
    const data = JSON.parse(event.content) as unknown;
    puzzle = validatePuzzle(data);
  } catch {
    return null;
  }

  // Use the title from the tag if the content title is empty.
  const titleTag = event.tags.find(([n]) => n === 'title')?.[1];
  if (titleTag && (!puzzle.title || puzzle.title === 'Untitled Puzzle')) {
    puzzle = { ...puzzle, title: titleTag };
  }

  const naddr = nip19.naddrEncode({
    kind: PUZZLE_KIND,
    pubkey: event.pubkey,
    identifier: dTag,
    relays: [],
  });

  return { event, puzzle, naddr, dTag, pubkey: event.pubkey };
}

// ─── Hooks ──────────────────────────────────────────────────────────────────

/** Fetch puzzles published on Nostr, tagged with the somaria-tiles tag. */
export function usePuzzles(limit = 50) {
  const { nostr } = useNostr();

  return useQuery<PuzzleEvent[]>({
    queryKey: ['puzzles', 'browse', limit],
    queryFn: async (c) => {
      const events = await nostr.query(
        [{ kinds: [PUZZLE_KIND], '#t': [PUZZLE_TAG], limit }],
        { signal: withTimeout(c.signal, 10000) },
      );

      // Deduplicate by pubkey + d-tag (keep latest).
      const map = new Map<string, PuzzleEvent>();
      for (const event of events) {
        const parsed = parsePuzzleEvent(event);
        if (!parsed) continue;
        const key = `${parsed.pubkey}:${parsed.dTag}`;
        const existing = map.get(key);
        if (!existing || event.created_at > existing.event.created_at) {
          map.set(key, parsed);
        }
      }

      return [...map.values()].sort((a, b) => b.event.created_at - a.event.created_at);
    },
    staleTime: 60 * 1000,
  });
}

/** Fetch a single puzzle by its naddr identifier. */
export function usePuzzleByNaddr(naddr: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<PuzzleEvent | null>({
    queryKey: ['puzzles', 'naddr', naddr ?? ''],
    queryFn: async (c) => {
      if (!naddr) return null;

      let decoded;
      try {
        decoded = nip19.decode(naddr);
      } catch {
        return null;
      }

      if (decoded.type !== 'naddr') return null;
      const { pubkey, identifier, kind } = decoded.data;

      if (kind !== PUZZLE_KIND) return null;

      const [event] = await nostr.query(
        [{ kinds: [PUZZLE_KIND], authors: [pubkey], '#d': [identifier], limit: 1 }],
        { signal: withTimeout(c.signal, 10000) },
      );

      if (!event) throw new Error('Puzzle not found');
      return parsePuzzleEvent(event);
    },
    enabled: !!naddr,
    staleTime: 5 * 60 * 1000,
    retry: 5,
    retryDelay: (attempt) => Math.min(1000 * 2 ** attempt, 8000),
  });
}

/** Fetch puzzles authored by a specific pubkey. */
export function usePuzzlesByAuthor(pubkey: string | undefined) {
  const { nostr } = useNostr();

  return useQuery<PuzzleEvent[]>({
    queryKey: ['puzzles', 'author', pubkey ?? ''],
    queryFn: async (c) => {
      if (!pubkey) return [];

      const events = await nostr.query(
        [{ kinds: [PUZZLE_KIND], authors: [pubkey], '#t': [PUZZLE_TAG], limit: 50 }],
        { signal: withTimeout(c.signal, 10000) },
      );

      const map = new Map<string, PuzzleEvent>();
      for (const event of events) {
        const parsed = parsePuzzleEvent(event);
        if (!parsed) continue;
        const key = `${parsed.pubkey}:${parsed.dTag}`;
        const existing = map.get(key);
        if (!existing || event.created_at > existing.event.created_at) {
          map.set(key, parsed);
        }
      }

      return [...map.values()].sort((a, b) => b.event.created_at - a.event.created_at);
    },
    enabled: !!pubkey,
    staleTime: 60 * 1000,
  });
}

/** Publish a puzzle to Nostr as a kind 39443 addressable event. */
export function usePublishPuzzle() {
  const { mutateAsync: publishEvent } = useNostrPublish();

  return useMutation<NostrEvent, Error, { puzzle: PuzzleData; dTag: string }>({
    mutationFn: async ({ puzzle, dTag }) => {
      const tags: string[][] = [
        ['d', dTag],
        ['t', PUZZLE_TAG],
        ['title', puzzle.title],
      ];

      if (puzzle.somaria) {
        tags.push(['somaria', 'true']);
      }

      if (puzzle.description) {
        tags.push(['description', puzzle.description.slice(0, 280)]);
      }

      // Grid dimensions for relay-level filtering.
      tags.push(['size', `${puzzle.width}x${puzzle.height}`]);

      // alt tag (NIP-31) for human-readable description.
      tags.push(['alt', `Somaria Tiles puzzle: ${puzzle.title}`]);

      const content = JSON.stringify(puzzle);

      return publishEvent({ kind: PUZZLE_KIND, content, tags });
    },
  });
}

/**
 * Delete a puzzle by publishing a NIP-09 deletion event (kind 5).
 * Uses the `a` tag format `<kind>:<pubkey>:<d-tag>` for addressable events.
 */
export function useDeletePuzzle() {
  const { mutateAsync: publishEvent } = useNostrPublish();
  const queryClient = useQueryClient();

  return useMutation<NostrEvent, Error, { pubkey: string; dTag: string }>({
    mutationFn: async ({ pubkey, dTag }) => {
      // NIP-09: for addressable events, use an `a` tag.
      const aTag = `${PUZZLE_KIND}:${pubkey}:${dTag}`;

      return publishEvent({
        kind: 5,
        content: '',
        tags: [
          ['a', aTag],
          ['t', PUZZLE_TAG],
        ],
      });
    },
    onSuccess: () => {
      // Invalidate all puzzle queries so deleted puzzles disappear.
      queryClient.invalidateQueries({ queryKey: ['puzzles'] });
    },
  });
}
